/**
 * Per-column min-width floor for the report grid.
 *
 * Computes a `min-width` CSS rule (no max-width) for each column, sized to
 * the measured pixel width of the column's header label. This prevents
 * columns from rendering narrower than their header text while allowing the
 * user to shrink or widen freely from that floor.
 *
 * CSS is injected into document.adoptedStyleSheets with a scoped descendant
 * selector so it reaches regular-table's light-DOM cells. Shadow-root
 * adoptedStyleSheets do not match those cells.
 */

/** Scope minimum widths to the report's light-DOM table. */
const REPORT_SCOPE =
  '[data-component=".report"] perspective-viewer-datagrid regular-table table'

/** Map visible column paths to regular-table's stable sizing classes. */
export const reportColumnPositions = (
  paths: readonly string[],
  rowHeaderCount: number
): ReadonlyArray<{ readonly field: string; readonly sizeKey: number }> =>
  paths.map((path, index) => ({
    field: path.slice(path.lastIndexOf('|') + 1),
    sizeKey: rowHeaderCount + index
  }))

/** Padding added to each measured label width: px-2 padding (16px) + sort indicator / column-resize slack (16px). */
export const HEADER_PADDING_PX = 32

/**
 * Returns the measured pixel width of the given text rendered in the given font.
 *
 * Accepts a `CanvasRenderingContext2D` so the function is pure and testable
 * without a real browser — callers supply the context; tests supply a stub.
 */
export const measureLabelWidth = (
  label: string,
  ctx: CanvasRenderingContext2D
): number => {
  const measured = ctx.measureText(label).width

  return Math.ceil(measured) + HEADER_PADDING_PX
}

/**
 * Returns the font string to use for measuring report header labels.
 *
 * Reads `getComputedStyle` from a reference element when available; falls back
 * to a reasonable default that matches the project's `--font-sans` stack at
 * the header font-size used in `_report.css` (12px / 500 weight).
 */
export const headerFont = (referenceEl: Element | null): string => {
  if (!referenceEl) {
    return '500 12px system-ui, sans-serif'
  }

  const style = getComputedStyle(referenceEl)
  const weight = style.fontWeight || '500'
  const size = style.fontSize || '12px'
  const family = style.fontFamily || 'system-ui, sans-serif'

  return `${weight} ${size} ${family}`
}

/**
 * Builds a map of column field name → min-width floor in px.
 *
 * Data-only carrier fields are excluded because they have no rendered columns.
 *
 * @param columns     Ordered list of column field names (from INITIAL_CONFIG).
 * @param labelOf     Returns the header label string for a given field name.
 * @param ctx         Canvas 2D context used for text measurement.
 * @param idColumns   Column field names to exclude (hidden id columns).
 */
export const buildColumnMinWidths = (
  columns: readonly string[],
  labelOf: (field: string) => string,
  ctx: CanvasRenderingContext2D,
  idColumns: readonly string[] = []
): ReadonlyMap<string, number> => {
  const idSet = new Set(idColumns)

  const entries = columns.flatMap((col): [string, number][] => {
    if (idSet.has(col)) {
      return []
    }

    return [[col, measureLabelWidth(labelOf(col), ctx)]]
  })

  return new Map(entries)
}

/**
 * Emits CSS `min-width`-only rules (no `max-width`) for each visible column
 * using regular-table's stable column classes across the whole view.
 *
 * The caller derives the row-header offset from getMeta().size_key - x.
 *
 * Only emits a rule for columns that have a positive min-width value in the
 * minWidths map.
 *
 * @param headerPositions  Full view's fields and stable column size keys
 * @param minWidths        Map of field → min-width in px
 * @param scopePrefix      Ancestor selector — defaults to REPORT_SCOPE
 */
export const buildMinWidthCssRules = (
  headerPositions: ReadonlyArray<{
    readonly field: string
    readonly sizeKey: number
  }>,
  minWidths: ReadonlyMap<string, number>,
  scopePrefix = REPORT_SCOPE
): string => {
  const rules = headerPositions.flatMap(({ field, sizeKey }): string[] => {
    const width = minWidths.get(field)

    if (!width || width <= 0) {
      return []
    }

    const selector = `${scopePrefix} thead tr.rt-autosize th.rt-col-${sizeKey}:not(.psp-is-width-override)`

    return [`${selector} { min-width: ${width}px }`]
  })

  return rules.join('\n')
}

/**
 * Injects (or replaces) the min-width floor stylesheet into document.adoptedStyleSheets.
 *
 * Regular-table renders its <table> in the light DOM, so a global document
 * stylesheet with a scoped descendant selector is required — injecting into
 * a shadowRoot.adoptedStyleSheets matches nothing on the cells.
 *
 * Returns the injected CSSStyleSheet (or null if cssText is empty) so callers
 * can replace it on the next call (e.g. when group_by depth changes).
 */
export const injectMinWidthSheet = (
  cssText: string,
  prevSheet: CSSStyleSheet | null
): CSSStyleSheet | null => {
  if (prevSheet !== null) {
    document.adoptedStyleSheets = document.adoptedStyleSheets.filter(
      (s) => s !== prevSheet
    )
  }

  if (cssText === '') {
    return null
  }

  const sheet = new CSSStyleSheet()
  sheet.replaceSync(cssText)
  document.adoptedStyleSheets = [...document.adoptedStyleSheets, sheet]

  return sheet
}
